import React from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import Modal from 'modules/views/shared/Modal';
import { ReactComponent as ExclamationRound } from 'assets/images/ExclamationRound.svg';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { deleteWorkerThirdPartyDrugTestResult } from 'modules/state-mgmt/workerData';

import { actionMapForOtherLoaders } from 'constants/actions';
import { useStylesDeleteCertificationModal } from 'modules/views/pages/Certifications/styles';
import { IThirdPartyTestResult } from '../types';

interface IDeleteTestResultModalProps {
  testResult: IThirdPartyTestResult | null;
  isVisible: boolean;
  onClose: () => void;
  onDeleteSuccess: () => void;
}

const DeleteTestResultModal = ({ testResult, isVisible, onClose, onDeleteSuccess }: IDeleteTestResultModalProps) => {
  const { t } = useTranslation();
  const classes = useStylesDeleteCertificationModal();
  const dispatch = useAppDispatch();
  const isDeleteLoading = useAppSelector(
    state => state.loader.otherLoaders[actionMapForOtherLoaders.deleteWorkerThirdPartyDrugTestResult]?.isLoading,
  );

  const onDeleteTestResult = () => {
    if (!testResult) return;
    dispatch(deleteWorkerThirdPartyDrugTestResult({ resultId: testResult.id }))
      .then(res => {
        if (res.type.includes('fulfilled')) {
          enqueueSnackbar(t('myDrugTesting.deleteResultModal.successMessage'), { variant: 'success' });
          onDeleteSuccess();
          onClose();
        } else {
          enqueueSnackbar(t('myDrugTesting.deleteResultModal.errors.failedToDelete'), { variant: 'error' });
        }
      })
      .catch(() => {
        enqueueSnackbar(t('myDrugTesting.deleteResultModal.errors.failedToDelete'), { variant: 'error' });
      });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Modal
      isVisible={isVisible}
      modalStyleClass={classes.modal}
      modalTitleStyleClass={classes.modalTitle}
      modalContentStyleClass={classes.modalContent}
      modalActionsStyleClass={classes.modalActions}
      testId="delete-test-result-modal"
      Content={
        <>
          <div className={classes.imageParent}>
            <ExclamationRound />
          </div>
          <p className={classes.title}>{t('myDrugTesting.deleteResultModal.title')}</p>
          <p className={classes.warningText}>{t('myDrugTesting.deleteResultModal.message')}</p>
        </>
      }
      actionButtons={[
        {
          label: t('common.cancel'),
          variant: 'outlined',
          styleClass: classes.button,
          testId: 'delete-test-result-cancel-button',
          onClick: onClose,
        },
        {
          label: t('common.delete'),
          styleClass: classes.button,
          testId: 'delete-test-result-confirm-button',
          isLoading: isDeleteLoading,
          onClick: onDeleteTestResult,
        },
      ]}
      onClose={onClose}
    />
  );
};

export default React.memo(DeleteTestResultModal);
